/**
 * Proof artefacts for the marketing home — the certified-document plates
 * graduated from the /concepts/proof board. The scoped stylesheet and
 * engraved figures are imported from the concept source so the two stay
 * in lockstep; this module only assembles the plates and adapts the
 * estates pair to the shipped connector story (Xero + SunSystems).
 *
 * Figures on the plates are representative renderings — the measured
 * claims they illustrate live in the surrounding section copy.
 */

import type { ReactNode } from "react";
import { Engraving } from "@/app/concepts/engraving";
import { CSS as PROOF_CSS, E } from "@/app/concepts/proof/_engravings";

/* ---- small inline marks (same geometry as the concept board) ---------- */

function Tick() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3.4 8.4 L6.5 11.5 L12.6 4.7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ---- the reconciliation ledger ---------------------------------------- */

type Row = { k: string; m: string; g: string; kind?: "subtotal" | "net" };
const LEDGER: Row[] = [
  { k: "Revenue", m: "£2,480,000.00", g: "£2,480,000.00" },
  { k: "Cost of sales", m: "£(1,041,600.00)", g: "£(1,041,600.00)" },
  { k: "Gross profit", m: "£1,438,400.00", g: "£1,438,400.00", kind: "subtotal" },
  { k: "Admin expenses", m: "£(612,300.00)", g: "£(612,300.00)" },
  { k: "Payroll", m: "£(548,900.00)", g: "£(548,900.00)" },
  { k: "Depreciation", m: "£(72,400.00)", g: "£(72,400.00)" },
  { k: "Operating profit", m: "£204,800.00", g: "£204,800.00", kind: "subtotal" },
  { k: "Taxation", m: "£(51,200.00)", g: "£(51,200.00)" },
  { k: "Net profit", m: "£153,600.00", g: "£153,600.00", kind: "net" },
];

export function ReconciliationPlate() {
  return (
    <div className="plate">
      <div className="plate-top">
        <div className="pt-block">
          <div className="pt-row">
            <span className="pt-k">Drawing</span>
            <span className="pt-v">MLX-PROOF-001</span>
          </div>
          <div className="pt-row">
            <span className="pt-k">Method</span>
            <span className="pt-v">Checked line by line</span>
          </div>
          <div className="pt-row">
            <span className="pt-k">Status</span>
            <span className="pt-v verified">
              <Tick />
              Finance verified
            </span>
          </div>
        </div>
        <div className="pt-seal">
          <Engraving data={E.pennySeal!} />
          <span className="seal-in">
            <Tick />
            <span className="seal-txt">
              To the
              <br />
              penny
            </span>
          </span>
        </div>
      </div>

      <div className="ledger">
        <div className="lgrid ledger-head" aria-hidden="true">
          <span className="lh">Line item</span>
          <span className="lh r model">Twin result</span>
          <span className="lh lead-h" />
          <span className="lh r truth-h">Approved figure</span>
          <span className="lh r delta">Δ Delta</span>
        </div>

        {LEDGER.map((row, i) => (
          <div className="lgrid lrow" data-kind={row.kind} key={row.k}>
            <span className="l-item">{row.k}</span>
            <span className="l-val model">{row.m}</span>
            <span className="l-lead" aria-hidden="true" />
            <span className="l-val truth">{row.g}</span>
            <span className="l-delta">
              <span className="dz">£0.00</span>
              <span className="dtick" style={{ ["--i" as string]: i }}>
                <Tick />
              </span>
            </span>
          </div>
        ))}
      </div>

      <div className="ledger-foot">
        <span>Checked line by line · 9 of 9 lines matched</span>
        <span>
          Largest variance <b>£0.00</b>
        </span>
      </div>
    </div>
  );
}

/* ---- the live estates pair -------------------------------------------- */
/* Adapted from the concept board: one estate runs modern cloud accounting
   (Xero), the other a legacy on-prem ledger (SunSystems) — matching the
   connector story the rest of the page commits to. */

const ESTATES = [
  {
    name: "Modern cloud accounting",
    source: "Xero",
    icon: "xero.svg" as string | null,
    mark: null as string | null,
    metric: (
      <>
        Gross margin <b>58.0%</b>, calculated from{" "}
        <span className="rel">
          Revenue <span className="arrow">▸</span> Cost of sales
        </span>
      </>
    ),
  },
  {
    name: "Legacy accounting system",
    source: "SunSystems",
    icon: "sunsystems.svg" as string | null,
    mark: null as string | null,
    metric: (
      <>
        Aged creditors <b>£412,806.00</b>, calculated from{" "}
        <span className="rel">
          Ledger <span className="arrow">▸</span> Purchases
        </span>
      </>
    ),
  },
];

export function LiveEstates() {
  return (
    <div>
      <div className="estates">
        {ESTATES.map((estate) => (
          <div className="estate" key={estate.name}>
            <div className="estate-top">
              <div className="estate-src">
                {estate.icon ? (
                  <img
                    src={`/concepts/source-icons/${estate.icon}`}
                    width={30}
                    height={30}
                    className="brand-svg"
                    alt=""
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 30,
                      height: 30,
                      display: "grid",
                      placeItems: "center",
                      border: "1px solid var(--hair)",
                      fontSize: 8,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      color: "var(--ink-62)",
                    }}
                  >
                    {estate.mark}
                  </span>
                )}
                <span className="es-name">
                  <b>{estate.name}</b>
                  <span>Connected · {estate.source}</span>
                </span>
              </div>
              <span className="chip dot">Read-only connection</span>
            </div>
            <div className="estate-body">
              <div className="estate-meta">
                <div className="live-row">
                  <span className="live-dot" />
                  <span className="live-lbl">Business logic prepared</span>
                </div>
                <div className="estate-metric">{estate.metric}</div>
              </div>
            </div>
            <div className="estate-foot">
              <span className="chip">Source remains authoritative</span>
              <span className="chip solid">
                <Tick />
                Team reviewed
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- scope wrapper ----------------------------------------------------- */

/**
 * Injects the proof board's scoped stylesheet and provides the
 * `.concepts-proof` scope (paper ground + drafting dot grid). Wrap the
 * whole proof band in exactly one of these.
 */
export function ProofScope({ children }: { children: ReactNode }) {
  return (
    <div className="concepts-proof" style={{ minHeight: 0 }}>
      <style dangerouslySetInnerHTML={{ __html: PROOF_CSS }} />
      {children}
    </div>
  );
}
